<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { type StatisticsWizardParcelData } from '../../models';
import { useStatisticsWizardStore } from '../../stores';
import { AxialForm } from '@common';
import StatisticsSubLayerSelector from './StatisticsSubLayerSelector.vue';
import { LoadingCircular, useCropStore } from '@shared';
import { useStatisticsWizardParcelCard } from '../..';

const props = defineProps({
  parcel: {
    type: Object as PropType<StatisticsWizardParcelData>,
    required: true
  }
});

const statisticsWizardStore = useStatisticsWizardStore();

const cropStore = useCropStore();

const cropName = computed(() => props.parcel.currentCrop && cropStore.findCropNameByCode(props.parcel.currentCrop));

const { form, parcelItem, isValid, isInvalidInput, isInvalidStatisticsKind, sourceMissing } =
  useStatisticsWizardParcelCard(props.parcel);
</script>

<template>
  <v-card
    :loading="parcelItem.formIsLoading"
    variant="elevated"
    class="statistics-wizard-parcel-card">
    <template #prepend>
      <div
        class="statistics-wizard-parcel-card__icon"
        :class="{
          'statistics-wizard-parcel-card__icon--success': isValid,
          'statistics-wizard-parcel-card__icon--warning': isInvalidInput,
          'statistics-wizard-parcel-card__icon--error': isInvalidStatisticsKind
        }">
        <div
          v-if="parcel.svg"
          class="d-flex align-center justify-center w-100 h-100"
          v-html="parcel.svg" />
        <v-icon
          v-else
          style="opacity: 0.3"
          icon="mdi-image-off-outline"
          size="6rem"
          color="grey-lighten-1" />
        <v-icon
          class="position-absolute"
          style="filter: drop-shadow(0 0 8px white)"
          size="3rem" />
      </div>
    </template>
    <template #append>
      <div class="d-flex flex-column gap-1">
        <statistics-sub-layer-selector
          v-if="parcel.selectedLayerGroup"
          :selected-layer-group="parcel.selectedLayerGroup"
          @on-sub-layer-select="statisticsWizardStore.switchSubLayer"></statistics-sub-layer-selector>
        <v-btn
          variant="flat"
          size="x-small"
          :disabled="parcelItem.isLoading"
          :icon="true"
          @click.prevent.stop="statisticsWizardStore.zoomToExtent(parcelItem.bbox)">
          <v-icon
            icon="mdi-magnify-plus-outline"
            size="x-large" />
        </v-btn>
        <v-btn
          variant="flat"
          size="x-small"
          :disabled="parcelItem.isLoading"
          :icon="true"
          @click.prevent.stop="statisticsWizardStore.removeParcel(parcelItem)">
          <v-icon
            icon="mdi-close"
            size="x-large"
            color="error" />
        </v-btn>
      </div>
    </template>
    <template #title>
      <div class="statistics-wizard-parcel-card__title">
        <span>{{ parcelItem.name }}</span>
        <span class="text-grey ml-3 font-weight-light">{{ parcelItem.area }} ha</span>
        <div
          v-if="cropName"
          class="d-flex align-center text-grey">
          &nbsp;
          <v-icon
            size="small"
            icon="mdi-flower" />
          <span class="font-weight-light">{{ cropName }}</span>
        </div>
      </div>
    </template>
    <template #item>
      <div>
        <div
          v-if="parcelItem.isLoading"
          class="my-4">
          <loading-circular />
        </div>
        <div v-else>
          <div
            v-if="!parcelItem.blocked"
            class="mt-4">
            <axial-form
              v-if="form"
              id="statisticsWizardParcelForm"
              :form="form.element" />
          </div>
          <div
            v-else
            class="d-flex align-center ga-2">
            <v-icon
              color="error"
              size="small"
              icon="mdi-alert" />
            <p class="text-error">{{ $t('statistics.wizard.label.blockedParcel') }}</p>
          </div>
          <div
            v-if="sourceMissing"
            class="d-flex align-center ga-2">
            <v-icon
              color="error"
              size="small"
              icon="mdi-alert" />
            <p class="text-error py-2">{{ $t('statistics.wizard.label.sourceMissing') }}</p>
          </div>
        </div>
      </div>
    </template>
  </v-card>
</template>
